import { useQuery } from '@tanstack/react-query';

import type { ResponseError } from 'Backend/common/types';
import type { operations } from 'Models/users.api.types';

import Config from 'Config';
import { getWithoutOverride } from 'Redux/api';
import { catchExpectedErrors } from '../common/catchExpectedErrors';
import { getCurrentUserId } from 'Lib/Utils';

type ApiOperation = operations['getUser'];
export type ResponseStatusCode = keyof ApiOperation['responses'];
type ResponseBody<T extends ResponseStatusCode> = ApiOperation['responses'][T]['content']['application/json'];

const supportedStatusCodes = [200, 400, 403, 404, 500] as const;

const isSupportedStatusCode = (status: number): status is (typeof supportedStatusCodes)[number] =>
    supportedStatusCodes.some((expected) => status === expected);

export type ResponseSuccess = ResponseBody<200>;

export const getUser = async ({ userId }: { userId?: string } = {}): Promise<ResponseSuccess> => {
    const { data, status } = await catchExpectedErrors(() =>
        getWithoutOverride(`${Config.users_v1_api}/${userId || getCurrentUserId()}`, {}, null, true, true)
    );

    /* eslint-disable no-throw-literal -- Want to throw the response object directly and pass it on to react query */

    if (!isSupportedStatusCode(status)) {
        throw {
            title: 'Unknown error',
            detail: 'Something went wrong getting the user',
        } satisfies ResponseError;
    }
    switch (status) {
        case 200:
            return data as ResponseBody<typeof status> satisfies ResponseSuccess;
        case 400:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 403:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 404:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 500:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        default:
            // Default is unreachable. Typescript gives an error if it is not.
            throw status satisfies never;
    }

    /* eslint-enable no-throw-literal -- Want to throw the response object directly and pass it on to react query */
};

export const useGetUser = ({ userId, enabled = true }: { userId?: string; enabled?: boolean } = {}) =>
    useQuery<ResponseSuccess, ResponseError>({
        queryKey: ['getUser', userId],
        queryFn: () => getUser({ userId }),
        enabled,
        refetchOnWindowFocus: false,
    });
